import React, {useEffect, useState} from "react";
import {
	Alert,
	Autocomplete,
	Box,
	Button,
	Chip,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	IconButton,
	Switch,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import {Add, Delete, Edit} from "@mui/icons-material";
import {pageAuthorizedByGroup} from "../../fragments/PageAuthorization";
import {graphQLServices} from "../../../services/GraphQLServices";
import {GET_ADMIN_LOOKUP_TYPES, GET_ADMIN_LOOKUPS} from "../components/graphql/queries/gqlAdminSettings";
import {DELETE_ADMIN_LOOKUP, SAVE_ADMIN_LOOKUP} from "../components/graphql/mutations/gqlAdminMutations";
import {LookupItem} from "./adminTypes";

function AdminLookupsPageContent() {
	const [types, setTypes] = useState<string[]>([]);
	const [typesLoading, setTypesLoading] = useState(true);
	const [type, setType] = useState<string | null>(null);
	const [lookups, setLookups] = useState<LookupItem[]>([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [editing, setEditing] = useState<LookupItem | null>(null);
	const [saving, setSaving] = useState(false);

	const loadTypes = async () => {
		setTypesLoading(true);
		try {
			const result = await graphQLServices.documentsClient.query({
				query: GET_ADMIN_LOOKUP_TYPES,
				fetchPolicy: "no-cache",
			});
			const loaded: string[] = result.data.adminLookupTypes ?? [];
			setTypes(loaded);
			setType(current => current ?? loaded[0] ?? null);
		} catch (e: any) {
			setError(e.message);
		} finally {
			setTypesLoading(false);
		}
	};

	const loadLookups = async () => {
		if (!type) {
			setLookups([]);
			return;
		}
		setLoading(true);
		setError(null);
		try {
			const result = await graphQLServices.documentsClient.query({
				query: GET_ADMIN_LOOKUPS,
				variables: {type},
				fetchPolicy: "no-cache",
			});
			setLookups(result.data.adminLookups ?? []);
		} catch (e: any) {
			setError(e.message);
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		loadTypes();
	}, []);

	useEffect(() => {
		loadLookups();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [type]);

	const handleNew = () => {
		if (!type) return;
		setError(null);
		setEditing({id: 0, type, code: "", description: "", disabled: false});
	};

	const handleEdit = (lookup: LookupItem) => {
		setError(null);
		setEditing({...lookup});
	};

	const handleDelete = async (lookup: LookupItem) => {
		if (!window.confirm(`Delete "${lookup.description}"? Documents that already use this code keep it.`)) return;
		try {
			await graphQLServices.documentsClient.mutate({
				mutation: DELETE_ADMIN_LOOKUP,
				variables: {input: {id: lookup.id}},
			});
			await loadLookups();
			await loadTypes();
		} catch (e: any) {
			setError(e.message);
		}
	};

	const handleSave = async () => {
		if (!editing) return;
		setSaving(true);
		setError(null);
		try {
			await graphQLServices.documentsClient.mutate({
				mutation: SAVE_ADMIN_LOOKUP,
				variables: {
					input: {
						id: editing.id || undefined,
						type: editing.type,
						code: editing.code.trim(),
						description: editing.description.trim(),
						disabled: editing.disabled,
					},
				},
			});
			setEditing(null);
			await loadLookups();
			await loadTypes();
		} catch (e: any) {
			setError(e.message);
		} finally {
			setSaving(false);
		}
	};

	return (
		<Box sx={{p: 3}}>
			<Typography variant="h5" gutterBottom>Lookup Lists</Typography>
			<Box sx={{display: "flex", gap: 2, alignItems: "center", mb: 2}}>
				<Autocomplete
					freeSolo
					autoSelect
					options={types}
					loading={typesLoading}
					value={type}
					onChange={(_, value) => setType(value ? value.trim().toUpperCase() : null)}
					sx={{minWidth: 300}}
					renderInput={params =>
						<TextField {...params} label="List" helperText="Select a list or type a new name"
							InputProps={{
								...params.InputProps,
								endAdornment: <>
									{typesLoading && <CircularProgress color="inherit" size={20} />}
									{params.InputProps.endAdornment}
								</>,
							}}
						/>}
				/>
				<Button startIcon={<Add />} variant="contained" onClick={handleNew} disabled={!type}>
					Add Item
				</Button>
			</Box>
			{error && !editing && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
			{loading || (typesLoading && !type) ? <CircularProgress /> :
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Code</TableCell>
							<TableCell>Description</TableCell>
							<TableCell>Status</TableCell>
							<TableCell>Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{lookups.length === 0 &&
							<TableRow><TableCell colSpan={4}>No items</TableCell></TableRow>}
						{lookups.map(lookup => (
							<TableRow key={lookup.id}>
								<TableCell>{lookup.code}</TableCell>
								<TableCell>{lookup.description}</TableCell>
								<TableCell>{lookup.disabled && <Chip size="small" label="Disabled" />}</TableCell>
								<TableCell>
									<IconButton size="small" onClick={() => handleEdit(lookup)}><Edit /></IconButton>
									<IconButton size="small" color="error" onClick={() => handleDelete(lookup)}><Delete /></IconButton>
								</TableCell>
							</TableRow>
						))}
					</TableBody>
				</Table>
			}

			<Dialog open={!!editing} onClose={() => setEditing(null)} maxWidth="sm" fullWidth>
				<DialogTitle>{editing?.id ? "Edit Item" : "New Item"}</DialogTitle>
				<DialogContent>
					{editing && (
						<Box sx={{mt: 1}}>
							{error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}
							<TextField fullWidth label="Code" value={editing.code}
								helperText="Stored on the document and used by SureBase Claims"
								onChange={e => setEditing({...editing, code: e.target.value})}
								sx={{mb: 2}} />
							<TextField fullWidth label="Description" value={editing.description}
								onChange={e => setEditing({...editing, description: e.target.value})}
								sx={{mb: 2}} />
							<FormControlLabel label="Disabled" control={
								<Switch checked={editing.disabled}
									onChange={e => setEditing({...editing, disabled: e.target.checked})} />
							} />
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setEditing(null)}>Cancel</Button>
					<Button variant="contained" onClick={handleSave}
						disabled={saving || !editing?.code.trim() || !editing?.description.trim()}>
						{saving ? "Saving..." : "Save"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
}

export const AdminLookupsPage = pageAuthorizedByGroup(AdminLookupsPageContent, ["Admin"]);
